<template>
  <form class="h-full w-full flex flex-col p-8" @submit.prevent="submitForm">
    <div class="grid grid-cols-1 gap-6">
      <div class="flex flex-col" :class="{ invalid: !eventType.isValid }">
        <label for="eventType">Event Type*</label>
        <select v-model="eventType.val" @blur="clearValidity('eventType')" class="outline-none flex-grow ht-42">
          <option value="">Select Event Type</option>
          <option value="Normal">Normal</option>
          <option value="Virtual Career Fair">Virtual Career Fair</option>
        </select>
        <transition name="fade" mode="out-in">
          <p v-if="!eventType.isValid" class="h-4 text-xs text-red-600">
            {{ eventType.msg }}
          </p>
        </transition>
      </div>
      <div v-if="eventType.val == 'Virtual Career Fair'" class="flex flex-col">
        <label for="virtualCareerFairEntity">Virtual Career Fair Entity*</label>
        <select v-model="virtualCareerFairEntity" class="outline-none flex-grow ht-42">
          <option value="">All</option>
          <option :value="item.entity" v-for="(item, index) in entities" :key="index">{{ getEntityName(item.entity) }}
          </option>
        </select>
      </div>
      <div v-if="eventType.val == 'Virtual Career Fair'" class="flex flex-col"
        :class="{ invalid: !selectedEmployers.isValid }">
        <label for="selectedEmployers">Virtual Career Fair Employers*</label>
        <multiselect v-model="selectedEmployers.val" :options="filteredPartnersList" :multiple="true" track-by="id"
          :custom-label="customLabel" placeholder="Select Virtual Career Fair Employers">
        </multiselect>
        <transition name="fade" mode="out-in">
          <p v-if="!selectedEmployers.isValid" class="h-4 text-xs text-red-600">
            {{ selectedEmployers.msg }}
          </p>
        </transition>
      </div>
      <div class="flex-shrink-0">
        <div v-if="thumbnailPreview" class="relative">
          <div @click.stop="cancelUploadedPhoto"
            class="absolute top-0 left-0 cursor-pointer h-6 w-6 border border-red-600 rounded-full flex flex-col items-center justify-center">
            <icon-close class="h-4 w-4 text-red-600"></icon-close>
          </div>
          <img class="h-48 object-contain" :src="thumbnailPreview" />
        </div>
        <img v-else class="h-48" src="../../assets/images/default/thumbnail.jpeg" />
        <div class="flex justify-start mt-4">
          <input ref="thumbnailPicture" class="hidden" id="profile-picture" @change="handleUploadedPhoto" type="file"
            accept="image/*" />
          <div @click.stop="onPickFile" class="text-twelve flex items-center cursor-pointer">
            <icon-plus class="h-4 w-4"></icon-plus>
            <span class="ml-1">Upload Thumbnail</span>
          </div>
        </div>
        <transition name="fade" mode="out-in">
          <p v-if="!thumbnailPicture.isValid" class="h-4 text-xs text-red-600">
            {{ thumbnailPicture.msg }}
          </p>
        </transition>
      </div>
      <div class="flex flex-col" :class="{ invalid: !title.isValid }">
        <label for="title">Title*</label>
        <input type="text" id="title" v-model.trim="title.val" placeholder="" @blur="clearValidity('title')" />
        <transition name="fade" mode="out-in">
          <p v-if="!title.isValid" class="h-4 text-xs text-red-600">
            {{ title.msg }}
          </p>
        </transition>
      </div>
      <div class="flex flex-col" :class="{ invalid: !description.isValid }">
        <label for="description">Description*</label>
        <div class="h-32 rounded-lg">
          <textarea id="description" v-model.trim="description.val" @blur="clearValidity('description')" placeholder=""
            class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"></textarea>
        </div>
        <transition name="fade" mode="out-in">
          <p v-if="!description.isValid" class="h-4 text-xs text-red-600">
            {{ description.msg }}
          </p>
        </transition>
      </div>
      <div class="flex flex-col" :class="{ invalid: !content.isValid }">
        <label for="content">Content*</label>
        <div class="mt-1 event-quill-container" :class="{ 'event-quill-invalid': !content.isValid }">
          <QuillEditor
            ref="quillEditor"
            v-model:content="content.val"
            contentType="html"
            :options="editorOptions"
            theme="snow"
            @blur="clearValidity('content')"
          />
        </div>
        <transition name="form-fade" mode="out-in">
          <p v-if="!content.isValid" class="h-4 text-xs text-red-600 ml-1 mt-1">
            {{ content.msg }}
          </p>
        </transition>
      </div>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
      <div class="flex flex-col" :class="{ invalid: !location.isValid }">
        <label for="location">Location*</label>
        <input type="text" id="location" v-model.trim="location.val" placeholder="" @blur="clearValidity('location')" />
        <transition name="fade" mode="out-in">
          <p v-if="!location.isValid" class="h-4 text-xs text-red-600">
            {{ location.msg }}
          </p>
        </transition>
      </div>
      <div class="flex flex-col" :class="{ invalid: !startDate.isValid }">
        <label for="startDate">Start Date*</label>
        <date-picker v-model:value="startDate.val" format="YYYY-MM-DD HH:mm:ss" type="datetime" value-type="format"
          placeholder="Select Start Date"></date-picker>
        <transition name="fade" mode="out-in">
          <p v-if="!startDate.isValid" class="h-4 text-xs text-red-600">
            {{ startDate.msg }}
          </p>
        </transition>
      </div>
      <div class="flex flex-col" :class="{ invalid: !endDate.isValid }">
        <label for="endDate">End Date*</label>
        <date-picker v-model:value="endDate.val" format="YYYY-MM-DD HH:mm:ss" type="datetime" value-type="format"
          placeholder="Select End Date"></date-picker>
        <transition name="fade" mode="out-in">
          <p v-if="!endDate.isValid" class="h-4 text-xs text-red-600">
            {{ endDate.msg }}
          </p>
        </transition>
      </div>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6 mt-6">
      <div class="flex flex-col" :class="{ invalid: !entity.isValid }">
        <label for="entity">Entity*</label>
        <select v-model="entity.val" @blur="clearValidity('entity')" class="outline-none flex-grow ht-42">
          <option :value="null">Select an Entity</option>
          <option :value="item.id" v-for="(item, index) in entities" :key="index">{{ getEntityName(item.entity) }}
          </option>
        </select>
        <transition name="fade" mode="out-in">
          <p v-if="!entity.isValid" class="h-4 text-xs text-red-600">
            {{ entity.msg }}
          </p>
        </transition>
      </div>
      <div class="flex flex-col">
        <label for="focalPoint">Focal Point</label>
        <input type="text" id="focalPoint" v-model.trim="focalPoint.val" placeholder="" />
      </div>
      <div class="flex flex-col">
        <div class="hidden sm:block ht-30"></div>
        <div class="h-full flex justify-start items-center">
          <input type="checkbox" id="displayTime" v-model.trim="displayTime.val" />
          <label for="displayTime" style="margin-bottom: 0px;" class="ml-2">Display Time</label>
        </div>
      </div>
      <div class="flex flex-col">
        <div class="hidden sm:block ht-30"></div>
        <div class="h-full flex justify-start items-center">
          <input type="checkbox" id="displayDay" v-model.trim="displayDay.val" />
          <label for="displayDay" style="margin-bottom: 0px;" class="ml-2">Display Day</label>
        </div>
      </div>
      <div class="flex flex-col">
        <div class="hidden sm:block ht-30"></div>
        <div class="h-full flex justify-start items-center">
          <input type="checkbox" id="displayAsPopUp" v-model.trim="displayAsPopUp.val" />
          <label for="displayAsPopUp" style="margin-bottom: 0px;" class="ml-2">Display as Pop Up</label>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
      <div class="flex flex-col">
        <div class="flex justify-start items-center">
          <input type="checkbox" id="registrationOpen" v-model.trim="registrationOpen.val" />
          <label for="registrationOpen" style="margin-bottom: 0px;" class="ml-2">Registration Open</label>
        </div>
      </div>
      <div v-if="registrationOpen.val" class="flex flex-col" :class="{ invalid: !openForStudent.isValid }">
        <label for="openForStudents">Open For(Students)</label>
        <label v-for="(item, index) in openForStudentsList" :key="index">
          <input type="checkbox" v-model="openForStudents" :value="item" @blur="clearValidity('openForStudent')"
            class="mr-2" />{{ item }}
        </label>
        <transition name="form-fade" mode="out-in">
          <p v-if="!openForStudent.isValid" class="h-4 text-xs text-red-600">
            {{ openForStudent.msg }}
          </p>
        </transition>
      </div>
      <div v-if="registrationOpen.val" class="flex flex-col" :class="{ invalid: !openForEntity.isValid }">
        <label for="openForEntities">Open For(Entity)</label>
        <label v-for="(item, index) in entityList" :key="index">
          <input type="checkbox" v-model="openForEntities" :value="item" @blur="clearValidity('openForEntity')"
            class="mr-2" />{{ getEntityName(item) }}
        </label>
        <transition name="form-fade" mode="out-in">
          <p v-if="!openForEntity.isValid" class="h-4 text-xs text-red-600">
            {{ openForEntity.msg }}
          </p>
        </transition>
      </div>
      <div v-if="registrationOpen.val" class="flex flex-col">
        <div class="hidden sm:block ht-30"></div>
        <div class="h-full flex justify-start items-start">
          <input type="checkbox" id="sendEmail" v-model.trim="sendEmail" />
          <label for="sendEmail" style="margin-bottom: 0px;" class="ml-2">Send Email</label>
        </div>
      </div>
      <div v-if="registrationOpen.val" class="flex flex-col">
        <div class="flex flex-col">
          <label for="registrationType">Registration Type</label>
          <select v-model="registrationType.val" class="outline-none flex-grow ht-42">
            <option value="internal">Internal</option>
            <option value="external">External</option>
          </select>
        </div>
      </div>
      <div v-if="registrationType.val === 'external'" class="flex flex-col"
        :class="{ invalid: !registrationLink.isValid }">
        <label for="registrationLink">Registration Link</label>
        <input type="text" id="registrationLink" v-model.trim="registrationLink.val" placeholder=""
          @blur="clearValidity('registrationLink')" />
        <transition name="form-fade" mode="out-in">
          <p v-if="!registrationLink.isValid" class="h-4 text-xs text-red-600">
            {{ registrationLink.msg }}
          </p>
        </transition>
      </div>
      <div v-else class="flex flex-col">
        <div v-if="registrationOpen.val" class="flex flex-col">
          <label for="displayMessage">Display Message</label>
          <div class="h-32 rounded-lg">
            <textarea id="displayMessage" v-model.trim="displayMessage.val"
              placeholder="Type your message to display..."
              class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"></textarea>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full flex justify-center mt-8 pb-8">
      <div v-if="isLoading"
        class="w-36 border-2 font-medium border-ternary text-ternary bg-white rounded-lg px-4 py-2 cursor-pointer">
        <base-spinner-with-text class-list="h-6 w-6 text-ternary capitalize" :text="getLoadingBtnText"
          text-class-list="text-ternary"></base-spinner-with-text>
      </div>
      <button v-else
        class="w-36 border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-4 py-2 uppercase text-sm cursor-pointer">
        {{ getBtnText }}
      </button>
    </div>
  </form>
  <div class="h-full flex justify-center items-center" v-if="isLoading">
    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
  </div>
</template>

<script>
import gql from "graphql-tag";
import IconClose from "../../components/icons/IconClose.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
export default {
  name: "event-form",
  emits: ["created", "updated", "close"],
  props: ["mode", "selectedItem", "entities"],
  components: {
    IconClose,
    IconPlus,
    QuillEditor,
    Multiselect,
  },
  data() {
    return {
      thumbnailPicture: {
        val: null,
        isValid: true,
        msg: "",
      },
      thumbnailPreview: null,
      eventType: {
        val: "Normal",
        isValid: true,
        msg: "",
      },
      title: {
        val: "",
        isValid: true,
        msg: "",
      },
      description: {
        val: "",
        isValid: true,
        msg: "",
      },
      content: {
        val: "",
        isValid: true,
        msg: "",
      },
      location: {
        val: "",
        isValid: true,
        msg: "",
      },
      startDate: {
        val: null,
        isValid: true,
        msg: "",
      },
      endDate: {
        val: null,
        isValid: true,
        msg: "",
      },
      entity: {
        val: null,
        isValid: true,
        msg: "",
      },
      focalPoint: {
        val: "",
        isValid: true,
        msg: "",
      },
      displayTime: {
        val: true,
        isValid: true,
        msg: "",
      },
      displayDay: {
        val: true,
        isValid: true,
        msg: "",
      },
      displayAsPopUp: {
        val: false,
        isValid: true,
        msg: "",
      },
      registrationOpen: {
        val: false,
        isValid: true,
        msg: "",
      },
      openForStudentsList: [
        "Senior students (Students in the last year)",
        "Alumni",
      ],
      openForStudents: [],
      openForStudent: {
        val: "",
        isValid: true,
        msg: "",
      },
      entityList: this.entities.map((item) => item.entity),
      openForEntities: [],
      openForEntity: {
        val: "",
        isValid: true,
        msg: "",
      },
      registrationType: {
        val: "internal",
        isValid: true,
        msg: "",
      },
      registrationLink: {
        val: "",
        isValid: true,
        msg: "",
      },
      displayMessage: {
        val: "",
        isValid: true,
        msg: "",
      },
      sendEmail: false,
      formIsValid: true,
      isLoading: false,
      partnersList: [],
      filteredPartnersList: [],
      selectedEmployers: {
        val: [],
        isValid: true,
        msg: "",
      },
      virtualCareerFairEntity: "",
    };
  },
  created() {
    this.getPartnersList();
    if (this.mode == "edit") {
      this.setFormData();
    }
  },
  watch: {
    'selectedEmployers.val': function (newVal) {
      if (newVal.length > 0) {
        this.selectedEmployers.isValid = true;
        this.selectedEmployers.msg = "";
      }
    },
    virtualCareerFairEntity(newVal) {
      this.selectedEmployers.val = [];
      if (newVal) {
        this.filteredPartnersList = this.partnersList.filter(partner =>
          partner.entity.split(',').includes(newVal)
        );
      } else {
        this.filteredPartnersList = this.partnersList;
      }
    },
  },
  computed: {
    getBtnText() {
      if (this.mode == "create") {
        return "Create";
      } else {
        return "Update";
      }
    },
    getLoadingBtnText() {
      if (this.mode == "create") {
        return "Creating...";
      } else {
        return "Updating...";
      }
    },
    editorOptions() {
      var self = this;
      return {
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              ["bold", "italic", "underline", "strike"],
              [{ list: "ordered" }, { list: "bullet" }],
              [{ script: "sub" }, { script: "super" }],
              [{ indent: "-1" }, { indent: "+1" }],
              [{ direction: "rtl" }],
              [{ size: ["small", false, "large", "huge"] }],
              [{ color: [] }, { background: [] }],
              [{ font: [] }],
              [{ align: [] }],
              ["link", "image", "video"],
              ["blockquote", "code-block"],
              ["clean"],
            ],
            handlers: {
              image: function () {
                self.imageHandler();
              },
            },
          },
        },
        placeholder: "Write event content in English (LTR) or Arabic (RTL)...",
        theme: "snow",
      };
    },
  },
  methods: {
    getEntityName(entity) {
      if (entity == "ATHS") {
        return "ATS";
      } else {
        return entity;
      }
    },
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    isRichTextEmpty(html) {
      if (!html || typeof html !== "string") {
        return true;
      }
      var text = html
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
      return text === "";
    },
    imageHandler() {
      var self = this;
      var input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/png,image/jpeg,image/jpg,image/gif,image/webp");
      input.click();

      input.onchange = function () {
        var file = input.files && input.files[0] ? input.files[0] : null;
        if (!file) {
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          if (self.$swal) {
            self.$swal.fire({
              icon: "error",
              title: "File too large",
              text: "Image must be smaller than 5MB.",
            });
          }
          return;
        }

        if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
          if (self.$swal) {
            self.$swal.fire({
              icon: "error",
              title: "Invalid file type",
              text: "Only PNG, JPG, JPEG, GIF or WebP images are allowed.",
            });
          }
          return;
        }

        var reader = new FileReader();
        reader.onload = function (e) {
          var quillEditorRef = self.$refs.quillEditor;
          if (quillEditorRef && quillEditorRef.getQuill) {
            var quillEditor = quillEditorRef.getQuill();
            if (quillEditor) {
              var range = quillEditor.getSelection(true);
              var index = range ? range.index : 0;
              var imageUrl = e && e.target ? e.target.result : null;
              if (imageUrl) {
                quillEditor.insertEmbed(index, "image", imageUrl);
                quillEditor.setSelection(index + 1);
                self.clearValidity("content");
              }
            }
          }
        };
        reader.readAsDataURL(file);
      };
    },
    validateForm() {
      this.formIsValid = true;

      if (this.eventType.val === "") {
        this.eventType.isValid = false;
        this.eventType.msg = "Event Type must not be empty.";
        this.formIsValid = false;
      } else {
        if (this.eventType.val == 'Virtual Career Fair' && this.selectedEmployers.val.length == 0) {
          this.selectedEmployers.isValid = false;
          this.selectedEmployers.msg = "Select at least one employer.";
          this.formIsValid = false;
        }
      }
      if (this.title.val === "") {
        this.title.isValid = false;
        this.title.msg = "Title must not be empty.";
        this.formIsValid = false;
      }
      if (this.description.val === "") {
        this.description.isValid = false;
        this.description.msg = "Description must not be empty.";
        this.formIsValid = false;
      }
      if (this.isRichTextEmpty(this.content.val)) {
        this.content.isValid = false;
        this.content.msg = "Content must not be empty.";
        this.formIsValid = false;
      }
      if (this.location.val === "") {
        this.location.isValid = false;
        this.location.msg = "Location must not be empty.";
        this.formIsValid = false;
      }
      if (this.startDate.val === null) {
        this.startDate.isValid = false;
        this.startDate.msg = "Start Date must not be empty.";
        this.formIsValid = false;
      }
      if (this.endDate.val === null) {
        this.endDate.isValid = false;
        this.endDate.msg = "End Date must not be empty.";
        this.formIsValid = false;
      }
      if (this.entity.val === null) {
        this.entity.isValid = false;
        this.entity.msg = "You must select an entity.";
        this.formIsValid = false;
      }
      if (this.registrationOpen.val) {
        if (this.openForStudents.length <= 0) {
          this.openForStudent.isValid = false;
          this.openForStudent.msg = "You must select any of the option.";
          this.formIsValid = false;
        } else {
          this.openForStudent.val = this.openForStudents.join(",");
        }
        if (this.openForEntities.length <= 0) {
          this.openForEntity.isValid = false;
          this.openForEntity.msg = "You must select any of the option.";
          this.formIsValid = false;
        } else {
          this.openForEntity.val = this.openForEntities.join(",");
        }
        if (this.registrationType.val === "external") {
          if (this.registrationLink.val === "") {
            this.registrationLink.isValid = false;
            this.registrationLink.msg = "Registration Link must not be empty.";
            this.formIsValid = false;
          }
        }
      }
    },
    async submitForm() {
      this.validateForm();

      if (!this.formIsValid) {
        return;
      }

      const formData = {
        thumbnail_picture: this.thumbnailPicture.val,
        thumbnail: this.mode == "create" ? "" : this.selectedItem.thumbnail,
        event_type: this.eventType.val,
        selected_partner_ids: this.selectedEmployers.val.map(e => Number(e.id)),
        title: this.title.val,
        description: this.description.val,
        content: this.content.val,
        location: this.location.val,
        start_date: this.startDate.val,
        end_date: this.endDate.val,
        entity_id: parseInt(this.entity.val),
        focal_point: this.focalPoint.val,
        display_time: this.displayTime.val,
        display_day: this.displayDay.val,
        display_as_pop_up: this.displayAsPopUp.val,
        registration_open: this.registrationOpen.val,
        open_for_students: this.openForStudent.val,
        open_for_entity: this.openForEntity.val,
        send_email: this.sendEmail,
        registration_type: this.registrationType.val,
        registration_link: this.registrationLink.val,
        display_message: this.displayMessage.val,
      };

      const CREATE_MUTATION = gql`
        mutation CreateEvent($input: CreateEventInput!) {
          createEvent(input: $input) {
            error
            message
          }
        }
      `;

      const UPDATE_MUTATION = gql`
        mutation UpdateEvent($token: String!, $input: CreateEventInput!) {
          updateEvent(token: $token, input: $input) {
            error
            message
          }
        }
      `;

      this.isLoading = true;
      let error_msg = null;

      try {
        await this.$apollo
          .mutate({
            mutation: this.mode == "create" ? CREATE_MUTATION : UPDATE_MUTATION,
            variables: {
              token: this.mode == "edit" ? this.selectedItem.token : null,
              input: formData,
            },
          })
          .then((data) => {
            if (this.mode == "create") {
              if (data.data.createEvent.error) {
                error_msg = data.data.createEvent.message;
              }
            } else {
              if (data.data.updateEvent.error) {
                error_msg = data.data.updateEvent.message;
              }
            }
          })
          .catch((error) => {
            error_msg = error;
          });
      } catch (error) {
        error_msg = error.message || "Failed to authenticate, try later.";
      }

      if (error_msg === null) {
        if (this.mode == "create") {
          this.$emit("created");
        } else {
          this.$emit("updated");
        }
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              this.$emit("close");
            }
          });
      }

      this.isLoading = false;
    },
    setFormData() {
      if (this.selectedItem.thumbnail) {
        this.thumbnailPreview =
          process.env.VUE_APP_PUBLIC_URL + this.selectedItem.thumbnail;
      }
      this.eventType.val = this.selectedItem.event_type;
      if (this.eventType.val == 'Virtual Career Fair') {
        this.selectedEmployers.val = this.selectedItem.event_employers.map(item => ({
          id: item.partner_id.toString(),
          en_company_name: item.partner.en_company_name
        }));
      }
      this.title.val = this.selectedItem.title;
      this.description.val = this.selectedItem.description;
      this.content.val = this.selectedItem.content;
      this.location.val = this.selectedItem.location;
      this.startDate.val = this.selectedItem.start_date;
      this.endDate.val = this.selectedItem.end_date;
      this.entity.val = this.selectedItem.entity_id;
      this.focalPoint.val = this.selectedItem.focal_point;
      this.displayTime.val = this.selectedItem.display_time;
      this.displayDay.val = this.selectedItem.display_day;
      this.displayAsPopUp.val = this.selectedItem.display_as_pop_up;
      this.registrationOpen.val = this.selectedItem.registration_open;
      if (this.registrationOpen.val) {
        this.openForStudents = this.selectedItem.open_for_students.split(",");
        this.openForEntities = this.selectedItem.open_for_entity.split(",");
        this.registrationType.val = this.selectedItem.registration_type;
        if (this.registrationType.val == "external") {
          this.registrationLink.val = this.selectedItem.registration_link;
        } else {
          this.displayMessage.val = this.selectedItem.display_message;
        }
      }
    },
    onPickFile() {
      this.$refs.thumbnailPicture.click();
    },
    handleUploadedPhoto(event) {
      this.thumbnailPicture.isValid = true;
      this.thumbnailPicture.msg = "";
      const file = event.target.files[0];
      this.thumbnailPicture.val = file;
      if (file) {
        this.thumbnailPreview = URL.createObjectURL(file);
      }
    },
    cancelUploadedPhoto() {
      this.thumbnailPicture.val = null;
      this.thumbnailPreview = null;
    },
    async getPartnersList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetActivePartnersList {
              activePartners {
                data {
                  id
                  en_company_name
                  entity
                }
              }
            }
          `,
          fetchPolicy: "network-only",
        });
        this.filteredPartnersList = this.partnersList = data.activePartners.data.map(({ id, en_company_name, entity }) => ({ id, en_company_name, entity }));
      } catch (error) {
        console.log(error);
      }
    },
    customLabel(option) {
      return option.en_company_name;
    },
  },
};
</script>

<style lang="scss" scoped>
label {
  @apply mb-1 block text-sm;
}

input[type="text"],
input[type="email"],
input[type="number"],
input[type="password"],
input[type="date"],
select {
  @apply block w-full border rounded-lg py-2 px-2;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="date"]:focus,
select:focus {
  outline: none;
  border-color: #394867;
  box-shadow: 0 0 0 2px #394867;
}

.invalid label {
  @apply text-red-600;
}

.invalid input,
.invalid select {
  border: 1px solid red;
}

input[type="checkbox"] {
  @apply w-auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0px;
}

.ht-30 {
  height: 30px;
}

.ht-42 {
  height: 42px;
}

.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.5s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
  opacity: 0;
  height: 0px;
}

.event-quill-container {
  min-height: 420px;
}

.event-quill-container :deep(.ql-toolbar) {
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  border: 1px solid #d1d5db;
  background-color: #f9fafb;
}

.event-quill-container :deep(.ql-container) {
  min-height: 360px;
  font-size: 14px;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  border: 1px solid #d1d5db;
  border-top: none;
  background-color: #ffffff;
}

.event-quill-container :deep(.ql-editor) {
  min-height: 360px;
  line-height: 1.7;
}

.event-quill-container :deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #9ca3af;
}

.event-quill-container :deep(.ql-editor img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin: 0.5rem 0;
}

.event-quill-invalid :deep(.ql-toolbar),
.event-quill-invalid :deep(.ql-container) {
  border-color: #dc2626;
}
</style>

<style lang="scss">
.mx-input {
  height: 42px !important;
}

.mx-datepicker {
  width: 100% !important;
}
</style>
